import type { ComponentType, SVGProps } from 'react';
import * as LucideIcons from 'lucide-react';
import { cn } from './utils';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type IconColor = 'default' | 'muted' | 'accent' | 'primary' | 'inverse' | 'error' | 'success';

type LucideIcon = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

const iconMap = LucideIcons as unknown as Record<string, LucideIcon>;

/** Pick only the lucide icon components (exclude helpers like `createLucideIcon`). */
export type IconName = Exclude<
  keyof typeof LucideIcons,
  'createLucideIcon' | 'default' | 'icons' | 'LucideIcon' | 'LucideProps' | 'Icon'
>;

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'color' | 'name'> {
  name: IconName;
  size?: IconSize;
  color?: IconColor;
  'aria-label'?: string;
  /** Decorative by default — flips `aria-hidden="true"`. Pass `aria-label` to mark meaningful icons. */
  decorative?: boolean;
}

const sizePx: Record<IconSize, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
};

const colorStyles: Record<IconColor, string> = {
  default: 'text-neutral-700',
  muted: 'text-neutral-500',
  accent: 'text-accent',
  primary: 'text-primary',
  inverse: 'text-white',
  error: 'text-error-fg',
  success: 'text-success-fg',
};

export function Icon({
  name,
  size = 'md',
  color = 'default',
  decorative = true,
  className = '',
  ...rest
}: IconProps) {
  const LucideComponent = iconMap[name as string];
  if (!LucideComponent) {
    return null;
  }
  const cls = cn(colorStyles[color], className);
  const ariaProps = decorative
    ? { 'aria-hidden': true as const, focusable: false as const }
    : { role: 'img' };
  return (
    <LucideComponent
      size={sizePx[size]}
      className={cls}
      {...ariaProps}
      {...rest}
    />
  );
}
